import type { Contract, ContractKind, ContractStatus } from "@/api/contracts";
import type { Role } from "@/lib/colors";

/** The road to signature, in order. Ended states sit off the road. */
export const PROGRESS: ContractStatus[] = [
  "draft",
  "internal_review",
  "with_counterparty",
  "negotiating",
  "out_for_signature",
  "signed",
];
export const ENDED: ContractStatus[] = ["expired", "terminated", "cancelled"];

export const STATUS: Record<ContractStatus, { label: string; short: string; role: Role | null }> = {
  draft: { label: "Drafting", short: "Draft", role: "cyan" },
  internal_review: { label: "Internal review", short: "Review", role: "azure" },
  with_counterparty: { label: "With them for review", short: "With them", role: "violet" },
  negotiating: { label: "Negotiating", short: "Negotiating", role: "pink" },
  out_for_signature: { label: "Out for signature", short: "Signature", role: "purple" },
  signed: { label: "Signed", short: "Signed", role: "green" },
  expired: { label: "Expired", short: "Expired", role: null },
  terminated: { label: "Terminated", short: "Terminated", role: null },
  cancelled: { label: "Cancelled", short: "Cancelled", role: null },
};

export const KINDS: { value: ContractKind; label: string }[] = [
  { value: "msa", label: "MSA" },
  { value: "sow", label: "SOW" },
  { value: "nda", label: "NDA" },
  { value: "order_form", label: "Order form" },
  { value: "mou", label: "MoU" },
  { value: "dpa", label: "DPA" },
  { value: "amendment", label: "Amendment" },
  { value: "other", label: "Other" },
];
export const KIND_LABEL = Object.fromEntries(KINDS.map((k) => [k.value, k.label])) as Record<
  ContractKind,
  string
>;

export function statusColor(s: ContractStatus, alpha?: number): string {
  const r = STATUS[s].role;
  if (!r) return alpha == null ? "rgb(var(--mut-500))" : `rgb(var(--mut-500) / ${alpha})`;
  return alpha == null ? `rgb(var(--${r}))` : `rgb(var(--${r}) / ${alpha})`;
}

/** Whole days from today (local) to an ISO day; negative when past. */
export function daysUntil(day: string): number {
  const [y, m, d] = day.slice(0, 10).split("-").map(Number);
  const target = new Date(y, m - 1, d);
  const now = new Date();
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  return Math.round((target.getTime() - today.getTime()) / 86_400_000);
}

export function inDays(n: number): string {
  if (n === 0) return "today";
  if (n === 1) return "tomorrow";
  if (n === -1) return "yesterday";
  return n > 0 ? `in ${n} days` : `${-n} days ago`;
}

/** What last happened, in a few words, for list rows. */
export function lastUpdate(c: Contract): string | null {
  const e = c.last_event;
  if (!e) return null;
  const who = e.author_name?.split(" ")[0];
  switch (e.kind) {
    case "comment":
      return `${e.side === "them" ? "Them" : (who ?? "Us")}: ${e.body ?? ""}`;
    case "status":
      return e.body;
    case "version":
      return `Version ${e.meta?.version} uploaded${who ? ` by ${who}` : ""}`;
    case "terms":
      return `Terms updated: ${(e.meta?.changes ?? []).map((ch) => ch.field).join(", ")}`;
    case "milestone":
      return `Done: ${e.body}`;
    default:
      return `Opened${who ? ` by ${who}` : ""}`;
  }
}
